import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { postNamedCommand, postNamedQuery } from '../service'
import { useAppContext } from '../redux/slices/appContext'

const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled']
const SUMMARY_TERMINAL = ['completed', 'failed']

const QUERY_KEYS = {
  list: (userId) => ['denali/meetings', userId],
  detail: (meetingId) => ['denali/meeting', meetingId],
  transcript: (meetingId) => ['denali/meeting-transcript', meetingId],
  notifications: () => ['denali/meeting-notifications']
}

export const useListMeetings = (filters = {}, options = {}) => {
  const { userId } = useAppContext()

  return useQuery({
    queryKey: [...QUERY_KEYS.list(userId), filters],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'list-meetings', {
        userId: filters.userId ?? null,
        clientId: filters.clientId ?? null,
        status: filters.status ?? null,
        startDate: filters.startDate ?? null,
        endDate: filters.endDate ?? null,
        limit: filters.limit ?? 50,
        offset: filters.offset ?? 0
      })
      return data
    },
    refetchInterval: options.refetchInterval,
    refetchOnWindowFocus: false
  })
}

export const useGetMeeting = (meetingId, options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.detail(meetingId),
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-meeting', { meetingId })
      return data
    },
    enabled: !!meetingId && (options.enabled !== false),
    refetchInterval: (data) => {
      if (options.poll === false) return false
      if (!data?.status) return false
      if (!TERMINAL_STATUSES.includes(data.status)) return 3000
      const hasPendingSummaries = data.summaries?.some(s => !SUMMARY_TERMINAL.includes(s.status))
      return hasPendingSummaries ? 3000 : false
    },
    refetchOnWindowFocus: false
  })
}

export const useGetMeetingTranscript = (meetingId, options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.transcript(meetingId),
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-meeting-transcript', { meetingId })
      return data
    },
    enabled: !!meetingId && (options.enabled !== false)
  })
}

export const useCreateMeeting = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('denali', 'create-meeting', command)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/meetings'] })
    }
  })
}

export const useUpdateMeeting = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('denali', 'update-meeting', command)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['denali/meetings'] })
      if (data?.meetingId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(data.meetingId) })
      }
    }
  })
}

export const useCancelMeeting = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ meetingId }) => {
      const { data } = await postNamedCommand('denali', 'cancel-meeting', { meetingId })
      return data
    },
    onSuccess: (_, { meetingId }) => {
      queryClient.invalidateQueries({ queryKey: ['denali/meetings'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(meetingId) })
    }
  })
}

export const useUpdateMeetingNotes = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ meetingId, userNotes }) => {
      const { data } = await postNamedCommand('denali', 'update-meeting-notes', {
        meetingId,
        userNotes
      })
      return data
    },
    onSuccess: (_, { meetingId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(meetingId) })
    }
  })
}

export const useMeetingNotifications = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.notifications(), filters],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'list-meeting-notifications', {
        status: filters.status ?? null,
        limit: filters.limit ?? 50,
        offset: filters.offset ?? 0
      })
      return data
    },
    enabled: options.enabled !== false,
    refetchOnWindowFocus: false
  })
}

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ meetingNotificationId }) => {
      const { data } = await postNamedCommand('denali', 'mark-notification-read', {
        meetingNotificationId
      })
      return data
    },
    onMutate: async ({ meetingNotificationId }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.notifications() })
      const snapshot = queryClient.getQueriesData({ queryKey: QUERY_KEYS.notifications() })
      queryClient.setQueriesData({ queryKey: QUERY_KEYS.notifications() }, (old) => {
        if (!Array.isArray(old)) return old
        return old.filter(n => n.meetingNotificationId !== meetingNotificationId)
      })
      return { snapshot }
    },
    onError: (_err, _vars, context) => {
      context?.snapshot?.forEach(([key, data]) => queryClient.setQueryData(key, data))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications() })
    }
  })
}
